import { formatUtcInTimezone } from '../../lib/time.js';
import { countryName } from '../../lib/countries.js';
import { EmptyState } from '../ui/empty-state.jsx';
import { Flag } from '../ui/country-select.jsx';

const TIER_TONES = {
  // HQ — network root / management plane. Distinct rose tone so the
  // coordinator's own row reads as "the central authority" at a glance,
  // not just "another command node".
  HQ:    'border-rose-300/30 bg-rose-soft text-rose-700',
  C4ISR: 'border-accent/30 bg-accent-soft text-accent',
  C2:    'border-success/30 bg-success-soft text-success',
  C1:    'border-warning/30 bg-warning-soft text-warning',
};

export function TierBadge({ tier }) {
  const tone = TIER_TONES[tier] || 'border-line bg-canvas text-mist';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider ${tone}`}>
      <i className="fa-solid fa-server text-[9px]" aria-hidden="true" />
      {tier}
    </span>
  );
}

const STATUS_META = {
  planned:        { tone: 'border-line bg-canvas text-mist',                icon: 'fa-clock',         label: 'Planned' },
  active:         { tone: 'border-success/30 bg-success-soft text-success', icon: 'fa-circle-check',  label: 'Active' },
  suspended:      { tone: 'border-warning/30 bg-warning-soft text-warning', icon: 'fa-pause',         label: 'Suspended' },
  decommissioned: { tone: 'border-rose-200 bg-rose-soft text-rose-700',     icon: 'fa-power-off',     label: 'Decommissioned' },
};

export function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.planned;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}>
      <i className={`fa-solid ${meta.icon} text-[9px]`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function LocationCell({ node }) {
  const hasCountry = !!node.countryCode;
  const hasCoords = node.latitude != null && node.longitude != null;
  if (!hasCountry && !hasCoords) {
    return <span className="text-xs text-mist">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      {hasCountry && (
        <div className="flex items-center gap-2">
          <Flag code={node.countryCode} size={14} />
          <span className="text-[12px] text-ink">{countryName(node.countryCode)}</span>
        </div>
      )}
      {hasCoords && (
        <div className="font-mono text-[10px] text-mist">
          {node.latitude.toFixed(4)}°, {node.longitude.toFixed(4)}°
        </div>
      )}
    </div>
  );
}

function CertChip({ node, viewerTimezone }) {
  const c = node.certificates;
  if (!c) return <span className="text-mist">—</span>;
  if (c.active) {
    const daysLeft = Math.round((new Date(c.active.notAfter).getTime() - Date.now()) / 86_400_000);
    const tone =
      daysLeft <= 14
        ? 'border-warning/30 bg-warning-soft text-warning'
        : 'border-success/30 bg-success-soft text-success';
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
          <i className="fa-solid fa-shield-halved text-[9px]" aria-hidden="true" />
          {daysLeft <= 0 ? 'expired' : `${daysLeft}d left`}
        </span>
        <span className="text-[10px] text-mist">{formatUtcInTimezone(c.active.notAfter, viewerTimezone)}</span>
      </div>
    );
  }
  if (c.total > 0) {
    return (
      <span className="inline-flex w-fit items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
        <i className="fa-solid fa-ban text-[9px]" aria-hidden="true" />
        {c.total} historical
      </span>
    );
  }
  return (
    <span className="inline-flex w-fit items-center gap-1 rounded-full border border-rose-200 bg-rose-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
      <i className="fa-solid fa-circle-exclamation text-[9px]" aria-hidden="true" />
      No cert
    </span>
  );
}

export function NodeTable({ rows, viewerTimezone, onEdit, onIssueCert, onIssueBootstrap, onDelete }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="fa-network-wired"
        title="No nodes registered yet"
        description="Create your first middle-tier node to start enrolling the mesh."
      />
    );
  }
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
              <th className="px-5 py-3">Node</th>
              <th className="px-5 py-3" title="Numeric ID — paste into ad-tms-service .env NODE_ID">ID</th>
              <th className="px-5 py-3">Tier</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Certificate</th>
              <th className="px-5 py-3">Last seen</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => {
              const hasActiveCert = !!n.certificates?.active;
              return (
                <tr key={n.id} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                  <td className="px-5 py-3 align-middle">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
                        <i className="fa-solid fa-server" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono font-semibold text-ink">{n.name}</div>
                        {n.url && (
                          <div className="mt-0.5 truncate font-mono text-[11px] text-mist">{n.url}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <span className="font-mono text-sm text-ink">{n.id}</span>
                  </td>
                  <td className="px-5 py-3 align-middle"><TierBadge tier={n.tier} /></td>
                  <td className="px-5 py-3 align-middle"><StatusBadge status={n.status} /></td>
                  <td className="px-5 py-3 align-middle"><LocationCell node={n} /></td>
                  <td className="px-5 py-3 align-middle"><CertChip node={n} viewerTimezone={viewerTimezone} /></td>
                  <td className="px-5 py-3 align-middle text-xs text-mist">
                    {n.lastSeenAt ? formatUtcInTimezone(n.lastSeenAt, viewerTimezone) : '—'}
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onIssueCert(n)}
                        title={hasActiveCert ? 'Renew certificate' : 'Issue certificate'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-accent-soft hover:text-accent"
                      >
                        <i className={`fa-solid ${hasActiveCert ? 'fa-rotate-right' : 'fa-shield-halved'}`} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onIssueBootstrap(n)}
                        title="Bootstrap tokens — for ad-tms-service self-enrollment"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-accent-soft hover:text-accent"
                      >
                        <i className="fa-solid fa-key" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(n)}
                        title="Edit"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(n)}
                        disabled={hasActiveCert}
                        title={hasActiveCert ? 'Revoke active certificate first' : 'Delete'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-mist"
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
